import { useEffect, useMemo, useState } from 'react'
import { AuthenticatedImage } from '@/components/features/authenticated-image'
import { ImageLightboxDialog } from '@/components/features/image-lightbox-dialog'
import type { ChatImage } from '@/components/layout/chat-pane.types'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type MenuState = { x: number; y: number; index: number } | null

type ChatPostedImagesProps = {
  images: ChatImage[]
  onEditMessage?: () => void
}

let suppressUserBubbleClickUntil = 0

export function isUserBubbleClickSuppressed() {
  return Date.now() < suppressUserBubbleClickUntil
}

function suppressUserBubbleClick(ms = 400) {
  suppressUserBubbleClickUntil = Date.now() + ms
}

/**
 * Posted chat thumbs. Click opens the shared lightbox (does not start edit).
 * Right-click offers Open full size and Edit message.
 */
export function ChatPostedImages({ images, onEditMessage }: ChatPostedImagesProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [menu, setMenu] = useState<MenuState>(null)

  const lightboxItems = useMemo(
    () => images.map((img) => ({ src: img.dataUrl, altCaption: 'Attached image' })),
    [images],
  )

  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('click', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  if (!images.length) return null

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-1">
        {images.map((img, index) => (
          <button
            key={img.id}
            type="button"
            data-testid={testIds.chatPane.postedImage}
            title="Click to enlarge"
            aria-label="Open image preview"
            className={cn(
              'h-20 max-w-[160px] overflow-hidden rounded-md border border-primary/20 bg-transparent p-0',
              'cursor-zoom-in outline-none transition-[filter]',
              'hover:brightness-[1.03] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              suppressUserBubbleClick()
              setMenu(null)
              setLightboxIndex(index)
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setMenu({ x: e.clientX, y: e.clientY, index })
            }}
          >
            <AuthenticatedImage
              rawSrc={img.dataUrl}
              alt="Attached image"
              className="pointer-events-none h-20 max-w-[160px] object-cover"
              draggable={false}
            />
          </button>
        ))}
      </div>
      {menu ? (
        <div
          role="menu"
          data-testid={testIds.chatPane.postedImageMenu}
          className="fixed z-50 min-w-[10rem] rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-md"
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            data-testid={testIds.chatPane.postedImageMenuOpen}
            className="flex w-full rounded-sm px-2 py-1.5 text-left hover:bg-muted"
            onClick={() => {
              setLightboxIndex(menu.index)
              setMenu(null)
            }}
          >
            Open full size
          </button>
          {onEditMessage ? (
            <button
              type="button"
              role="menuitem"
              data-testid={testIds.chatPane.postedImageMenuEdit}
              className="flex w-full rounded-sm px-2 py-1.5 text-left hover:bg-muted"
              onClick={() => {
                setMenu(null)
                onEditMessage()
              }}
            >
              Edit message
            </button>
          ) : null}
        </div>
      ) : null}
      <ImageLightboxDialog
        src={lightboxIndex == null ? null : lightboxItems[lightboxIndex]?.src ?? null}
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        onClose={() => {
          suppressUserBubbleClick()
          setLightboxIndex(null)
        }}
        testId={testIds.chatPane.postedImageLightbox}
        captionTestId={testIds.chatPane.postedImageLightboxCaption}
        prevTestId={testIds.chatPane.postedImageLightboxPrev}
        nextTestId={testIds.chatPane.postedImageLightboxNext}
      />
    </>
  )
}
